/**
 * The public examples: a gallery, and one frozen result read without signing in.
 *
 * These reuse the Result page's own components, so what a stranger reads is the product rather than
 * a brochure imitating it. What they do not get is anything that acts: What if, Goal seek and the
 * downloads are gone, because each needs an account - which makes every one of them a reason to
 * make one, placed exactly where somebody has just understood why they would want it.
 *
 * The call to action is deliberately at the top and the bottom. Somebody convinced by the headline
 * figures should not have to scroll past the whole page to act, and somebody who read all of it
 * should not have to scroll back.
 */

import { ArrowRight, FlaskConical, Pause, Play, Volume2 } from "lucide-react";
import { Link, useParams } from "react-router";

import { Chart } from "@/charts/Chart";
import { phraseDrivers, stanceByRound } from "@/charts/options";
import { TableView } from "@/charts/TableView";
import { percent } from "@/charts/theme";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button, buttonVariants } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useMemo } from "react";

import { TourProvider, useNarratedExample, useTour } from "@/app/tour";
import { useBranding } from "@/lib/useBranding";
import { usePublishedExample, useShowcase } from "@/lib/queries";
import { Narrated } from "./Result";
import { intervalHelp, PANEL_HELP, TILE_HELP } from "./ResultHelp";
import { Help } from "@/components/Help";

function Backdrop() {
  const { brand } = useBranding();
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 bg-cover bg-center opacity-40"
      style={{ backgroundImage: `url(${brand.backdrop_url})` }}
    />
  );
}

function TopBar() {
  const { brand } = useBranding();
  return (
    <header className="relative z-10 border-b border-white/10 bg-black/60 backdrop-blur-sm">
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-6 py-3">
        <Link to="/">
          <img src={brand.logo_url} alt={brand.product_name} className="block h-8 w-auto" />
        </Link>
        <nav className="flex items-center gap-2 text-sm">
          <Link to="/examples" className="px-2 py-1 text-muted-foreground hover:text-foreground">
            Examples
          </Link>
          <Link to="/pricing" className="px-2 py-1 text-muted-foreground hover:text-foreground">
            Pricing
          </Link>
          <Link to="/login" className={buttonVariants({ size: "sm" })}>
            Sign in
          </Link>
        </nav>
      </div>
    </header>
  );
}

/** The whole reason the examples exist. Shown twice, because a reader may be convinced at either end. */
function TryYourOwn({ compact = false }: { compact?: boolean }) {
  return (
    <div
      className={`relative z-10 rounded-xl border border-white/12 bg-[rgb(8_11_15/0.78)] backdrop-blur-sm ${
        compact ? "p-4" : "p-6"
      }`}
    >
      <p className={compact ? "text-sm font-medium" : "text-lg font-semibold"}>
        Now run your own message past them.
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        Sign in and your first tests are on us — real runs, from the credit we give every new account.
      </p>
      <Link to="/login" className={`mt-3 ${buttonVariants()}`}>
        Run a free message test <ArrowRight aria-hidden="true" className="size-4" />
      </Link>
    </div>
  );
}


// --- the gallery -----------------------------------------------------------------------------------


export function ExamplesPage() {
  const gallery = useShowcase();

  return (
    <div className="relative min-h-dvh bg-black">
      <Backdrop />
      <TopBar />
      <main className="relative z-10 mx-auto max-w-5xl space-y-8 px-6 py-12">
        <header className="space-y-2">
          <h1 className="text-3xl font-semibold">Real message tests, start to finish</h1>
          <p className="max-w-2xl text-muted-foreground">
            Each of these is an actual run: a real message put to a census-grounded community of
            personas, with every figure, every post and every reason they gave. Nothing is signed in
            and nothing is edited.
          </p>
        </header>

        {gallery.isPending && <Skeleton className="h-40 w-full" />}
        {gallery.isError && (
          <Alert variant="destructive">
            <AlertDescription>The examples could not be loaded just now.</AlertDescription>
          </Alert>
        )}

        <div className="grid gap-4 md:grid-cols-3">
          {(gallery.data?.examples ?? []).map((example, index) => (
            <Link
              key={example.slug}
              to={`/examples/${example.slug}`}
              className="group rounded-xl border border-white/12 bg-[rgb(8_11_15/0.78)] p-5 backdrop-blur-sm transition-colors hover:border-white/30"
            >
              <p className="text-[11px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
                Example {index + 1}
              </p>
              <p className="mt-2 text-2xl font-semibold tabular-nums">{example.headline_figure}</p>
              <h2 className="mt-2 font-medium group-hover:underline">{example.title}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{example.summary}</p>
              <p className="mt-3 text-sm text-[var(--brand-primary,#3987e5)]">Read the whole run →</p>
            </Link>
          ))}
        </div>

        <TryYourOwn />
      </main>
    </div>
  );
}


// --- one published result ----------------------------------------------------------------------------


/** The narrated tour, as a tile. The same tour the product offers its own customers. */
function TourTile() {
  const tour = useTour();
  if (!tour.available) return null;

  const playing = tour.status === "playing";
  const busy = tour.status === "loading";
  const started = tour.clips.length > 0 && (playing || tour.status === "paused");

  return (
    <section
      aria-label="Narrated tour"
      className="rounded-xl border border-white/12 bg-[rgb(8_11_15/0.78)] p-4 backdrop-blur-sm"
    >
      <p className="text-[11px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
        Narrated tour
      </p>
      <p className="mt-1 text-sm text-slate-300">
        Hear what each figure means, and which panel it is about.
      </p>
      <Button
        className="mt-3 w-full justify-center"
        variant={started ? "outline" : "default"}
        disabled={busy}
        aria-label={playing ? "Pause the narrated tour" : "Play the narrated tour"}
        onClick={() => (playing ? tour.pause() : started ? tour.resume() : tour.start())}
      >
        {playing ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
        {busy ? "Preparing…" : playing ? "Pause" : started ? "Resume" : "Play"}
        <Volume2 aria-hidden="true" />
      </Button>

      {tour.clips.length > 0 && (
        <div className="mt-3 flex gap-1" role="group" aria-label="Jump to a figure">
          {tour.clips.map((clip, index) => (
            <button
              key={clip.key || "opening"}
              type="button"
              title={clip.title}
              aria-label={`Jump to ${clip.title}`}
              aria-current={index === tour.step}
              onClick={() => tour.jump(index)}
              className={`h-1.5 flex-1 cursor-pointer rounded-full transition-colors ${
                index === tour.step
                  ? "bg-[#0ca30c]"
                  : index < tour.step
                    ? "bg-white/35 hover:bg-white/50"
                    : "bg-white/12 hover:bg-white/25"
              }`}
            />
          ))}
        </div>
      )}

      {started && (
        <p className="mt-3 text-xs text-muted-foreground" aria-live="polite">
          {tour.clips[tour.step]?.text}
        </p>
      )}

      {tour.audioError && (
        <p className="mt-2 text-[11px] text-muted-foreground">
          No sound for this one: {tour.audioError}. The tour still runs, in writing.
        </p>
      )}
    </section>
  );
}

function ExampleBody() {
  const { slug } = useParams();
  const example = usePublishedExample(slug);
  const view = example.data?.dashboard;
  useNarratedExample(slug, Boolean(view));

  const stanceOption = useMemo(() => (view ? stanceByRound(view.stance_by_round, null) : null), [view]);
  const driversOption = useMemo(() => (view ? phraseDrivers(view.drivers) : null), [view]);

  return (
    <div className="relative min-h-dvh bg-black">
      <Backdrop />
      <TopBar />
      <main className="relative z-10 mx-auto max-w-6xl space-y-6 px-6 py-10">
        {example.isPending && <Skeleton className="h-64 w-full" />}
        {example.isError && (
          <Alert variant="destructive">
            <AlertDescription>
              That example could not be found. <Link to="/examples" className="underline">See the others</Link>.
            </AlertDescription>
          </Alert>
        )}

        {view && (
          <>
            <header className="space-y-2">
              <p className="text-sm text-muted-foreground">A published example · {view.n} simulated personas</p>
              <h1 className="text-2xl font-semibold">{example.data?.title}</h1>
              <p className="max-w-2xl text-muted-foreground">{example.data?.summary}</p>
            </header>

            <div className="grid gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
              {/* The controls sit beside the thing they describe, not above it: the tour highlights
                  panels on the right, and a reader should be able to see both at once. */}
              <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start">
                <TourTile />
                <TryYourOwn compact />
              </aside>

              <div className="space-y-6">
            {view.reliability.flagged > 0 && (
              <Alert>
                <FlaskConical aria-hidden="true" />
                <AlertDescription>
                  {view.reliability.flagged} of {view.reliability.personas} personas were flagged as
                  unreliable in validation
                  {view.reliability.excluded ? " and excluded from this simulation." : " but were still simulated."}
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {view.tiles.map((tile) => (
                <Narrated key={tile.key} narrates={tile.key} className="rounded-xl border bg-[#16181d] p-3">
                  <p className="text-xs text-muted-foreground">
                    {tile.label}
                    {TILE_HELP[tile.key] && (
                      <Help about={tile.label} text={TILE_HELP[tile.key]} note={intervalHelp(view.n)} />
                    )}
                  </p>
                  <p className="mt-1 text-2xl font-semibold tabular-nums">{tile.formatted}</p>
                  <p className="mt-1 text-[11px] text-muted-foreground">{tile.interval}</p>
                </Narrated>
              ))}
            </div>

            <Panel title="Stance by round" help={PANEL_HELP["Stance by round"]} narrates="stance_by_round">
              <Chart
                option={stanceOption!}
                height={Math.max(150, 60 * view.rounds)}
                description={`Share of personas at each stance, for ${view.rounds} round${view.rounds === 1 ? "" : "s"}`}
              />
              <TableView
                title="Stance by round"
                rows={view.stance_by_round.map((r) => ({
                  Round: r.round_label,
                  Stance: r.stance_label,
                  Personas: r.personas,
                  Share: percent(r.share),
                }))}
              />
            </Panel>

            <Panel title="Opinion shift" help={PANEL_HELP["Opinion shift"]} narrates="opinion_shift">
              {view.shift === null || view.rounds < 2 ? (
                <p className="text-sm text-muted-foreground">
                  Only one round was run, so there is no shift to show.
                </p>
              ) : (
                <>
                  <p className="text-3xl font-semibold tabular-nums">
                    {view.shift > 0 ? "+" : ""}
                    {view.shift.toFixed(2)}
                  </p>
                  <p className="text-xs text-muted-foreground">Mean stance change, round 1 → final</p>
                  <p className="mt-3 text-sm">
                    {view.moved_up} personas moved up and {view.moved_down} moved down after seeing
                    each other's posts.
                  </p>
                </>
              )}
            </Panel>

            {view.drivers.length > 0 && (
              <Panel title="What drove reactions" help={PANEL_HELP["What drove reactions"]}>
                <p className="mb-2 text-xs text-muted-foreground">
                  Sentences from the message that personas quoted word for word when saying why they
                  reacted as they did, each persona counted once per sentence.
                </p>
                <Chart
                  option={driversOption!}
                  height={Math.max(160, 42 * view.drivers.length)}
                  description="Scenario sentences personas quoted, split by where those personas ended up"
                />
              </Panel>
            )}

            <Panel title="What they said" help={PANEL_HELP["Posts"]}>
              <ul className="space-y-3">
                {view.posts.slice(0, 12).map((post) => (
                  <li key={post.persona_id} className="rounded-lg border border-white/8 bg-black/30 p-3">
                    <p className="text-xs text-muted-foreground">
                      {post.name || post.persona_id} · {post.stance_label}
                    </p>
                    <p className="mt-1 text-sm">{post.post}</p>
                  </li>
                ))}
              </ul>
              {view.posts.length > 12 && (
                <p className="mt-3 text-sm text-muted-foreground">
                  {view.posts.length - 12} more posts, and every persona's reasons, are in the full run.
                </p>
              )}
            </Panel>

            <p className="text-xs text-muted-foreground">
              These are simulated personas — a language model playing a part, not people. This is a
              recorded run, kept as it was; it is not a validated research instrument and its output
              is not a substitute for research with real people.
            </p>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export function PublishedExamplePage() {
  // Its own provider: the shell's belongs to the signed-in app, and this page is outside it.
  return (
    <TourProvider>
      <ExampleBody />
    </TourProvider>
  );
}

function Panel({ title, help, narrates, children }: {
  title: string;
  help?: string;
  narrates?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h2 className="mb-2 text-sm font-medium tracking-wide text-muted-foreground uppercase">
        {title}
        {help && <Help about={title.toLowerCase()} text={help} />}
      </h2>
      <Narrated narrates={narrates} className="rounded-xl border bg-[#16181d] p-4">
        {children}
      </Narrated>
    </section>
  );
}
